/**
 * Overlay / backdrop diagnostics -> Firebase Analytics (observe-only).
 *
 * The "frozen on Home after an App Store update" reports are NOT a boot hang:
 * Home renders fully, but the screen is dimmed and every tap is swallowed — the
 * signature of a STUCK / ORPHANED overlay backdrop (ion-backdrop) sitting on top
 * and intercepting pointer events. Boot-step diagnostics can't see this because
 * boot completes; this module inspects the live DOM instead.
 *
 * The decisive probe is `document.elementFromPoint(centre)`: it returns the
 * element that WOULD receive a tap at the middle of the screen. If that element
 * is an ion-backdrop (or any overlay) rather than app content, taps are being
 * blocked — and we learn the exact culprit element. This both CONFIRMS and, if
 * the top element is normal app content, REFUTES the stuck-overlay theory.
 *
 * Nothing here changes behaviour: it only reads the DOM and reports.
 */
import { diagEvent } from './diag'

const OVERLAY_TAGS =
  'ion-modal,ion-alert,ion-popover,ion-action-sheet,ion-loading,ion-picker,ion-toast'

// Any of these at screen-centre means app content is NOT reachable by a tap
// (a bare backdrop OR a stuck modal/alert/etc. both block interaction).
const OVERLAY_RE = /ion-backdrop|ion-modal|ion-alert|ion-popover|ion-loading|ion-action-sheet/

/** Compact "tag.class1.class2" description of an element (or "none"). */
function describe(el) {
  if (!el) return 'none'
  const tag = (el.tagName || '').toLowerCase()
  let cls = ''
  try {
    if (typeof el.className === 'string' && el.className.trim()) {
      cls = '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
    }
  } catch (_) {}
  return (tag + cls).slice(0, 90)
}

/** How many overlays are actually visible (Ionic hides mounted-but-closed ones). */
function countPresented() {
  let presented = 0
  try {
    document.querySelectorAll(OVERLAY_TAGS).forEach((m) => {
      try {
        if (m.classList.contains('overlay-hidden')) return
        const cs = getComputedStyle(m)
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0') {
          presented += 1
        }
      } catch (_) {}
    })
  } catch (_) {}
  return presented
}

/**
 * Snapshot the overlay/backdrop state and report it. `ctx` labels the trigger
 * (e.g. "resume", "perm_dismiss"). Emits a `diag_overlay` event with:
 *   ctx        - trigger label
 *   backdrops  - number of <ion-backdrop> in the DOM
 *   presented  - number of genuinely-visible overlays
 *   top        - element that would receive a centre-screen tap
 *   blocked    - 1 if taps look blocked (top is a backdrop, or a backdrop
 *                exists with nothing legitimately open), else 0
 */
export function snapshotOverlays(ctx) {
  try {
    const backdrops = document.querySelectorAll('ion-backdrop').length
    const presented = countPresented()

    // Compact inventory of the overlays actually on screen — so a backdrop-only
    // stranding still names which overlay(s) are up, not just the centre element.
    const inv = []
    try {
      document.querySelectorAll(OVERLAY_TAGS + ',ion-backdrop').forEach((m) => {
        try {
          if (m.classList && m.classList.contains('overlay-hidden')) return
          const cs = getComputedStyle(m)
          if (cs.display === 'none' || cs.visibility === 'hidden') return
          inv.push(describe(m))
        } catch (_) {}
      })
    } catch (_) {}
    const list = inv.slice(0, 4).join(',').slice(0, 90)

    const cx = Math.max(1, Math.floor(window.innerWidth / 2))
    const cy = Math.max(1, Math.floor(window.innerHeight / 2))
    const top = describe(document.elementFromPoint(cx, cy))

    // Taps are blocked if the centre element is ANY overlay (backdrop, modal,
    // alert, popover, loading, action-sheet), or a backdrop exists with nothing
    // legitimately open.
    const blocked = OVERLAY_RE.test(top) || (backdrops > 0 && presented === 0)

    diagEvent('diag_overlay', {
      ctx: String(ctx || '').slice(0, 36),
      backdrops,
      presented,
      top,
      list,
      hidden: document.hidden ? 1 : 0,
      blocked: blocked ? 1 : 0,
    })
  } catch (_) {}
}

let installed = false

/**
 * Snapshot overlays on every foreground/resume, and flag any tap that actually
 * lands on a backdrop. Idempotent — safe to call more than once. Native + web.
 */
export function installOverlayDiag() {
  if (installed) return
  installed = true

  let lastAt = 0
  const onResume = () => {
    // Debounce: visibilitychange + appStateChange can both fire on one resume.
    let now = 0
    try {
      now = window.performance ? window.performance.now() : 0
    } catch (_) {}
    if (now && now - lastAt < 500) return
    lastAt = now
    // Immediately, then again once the resume/animations settle — an orphaned
    // backdrop is often only observable a beat after the WebView wakes.
    snapshotOverlays('resume')
    setTimeout(() => snapshotOverlays('resume_1500'), 1500)
  }

  try {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) onResume()
    })
  } catch (_) {}

  // Capacitor App resume (native foreground, even when visibilitychange is
  // unreliable on iOS). Dynamic import so web/SSR never hard-depends on it.
  try {
    import('@capacitor/app')
      .then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) onResume()
        })
      })
      .catch(() => {})
  } catch (_) {}

  // Decisive live signal: a tap that lands ON a backdrop means the backdrop is
  // eating the user's touch. `presented === 0` alongside it => orphaned scrim.
  try {
    document.addEventListener(
      'pointerdown',
      (e) => {
        try {
          const t = e.target
          if (t && t.closest && t.closest('ion-backdrop')) {
            diagEvent('diag_overlay', {
              ctx: 'tap_on_backdrop',
              backdrops: document.querySelectorAll('ion-backdrop').length,
              presented: countPresented(),
              top: describe(t),
              blocked: 1,
            })
          }
        } catch (_) {}
      },
      true, // capture phase — see the tap before the backdrop consumes it
    )
  } catch (_) {}
}
